import type { Superficie } from "./db";

/**
 * Dove si usa ciascuna superficie che non è il menu d'ufficio.
 * Stesso login e stesso database: cambia solo l'indirizzo e la schermata.
 */
export interface InfoSuperficie {
  superficie: Exclude<Superficie, "ufficio">;
  percorso: string;
  nome: string;
  descrizione: string;
  /** app installabile sul telefono: file del manifest in public/ (id, start_url e scope propri: due app distinte per il telefono) */
  manifest?: string;
  /** app che non ha una funzione propria: entra chi vede almeno una di queste funzioni d'ufficio (QBI POSTA) */
  funzioni?: string[];
}

export const SUPERFICI: InfoSuperficie[] = [
  { superficie: "autista", percorso: "/autista", nome: "QBI ON ROAD",
    descrizione: "App autista da installare sul telefono", manifest: "/autista.webmanifest" },
  { superficie: "magazzino", percorso: "/magazzino", nome: "QBI ON MAG",
    descrizione: "App magazzino da installare sul telefono", manifest: "/magazzino.webmanifest" },
  { superficie: "schermo", percorso: "/live", nome: "QBI LIVE",
    descrizione: "Centro traffico per il megaschermo", manifest: "/live.webmanifest" },
  // Luan 02/11/2026 22:14: «crea app QBI POSTA». Stessa posta del menu Posta (email e PEC), a schermo intero, installabile su computer e telefono.
  { superficie: "posta", percorso: "/posta", nome: "QBI POSTA",
    descrizione: "Email e PEC, da installare su computer e telefono", manifest: "/posta.webmanifest", funzioni: ["email.posta", "email.pec", "sistema.posta"] },
  // Luan 04/11/2026 12:53: «un'app di scansione documenti… da telefono… ed averli direttamente in QBI» (211). Ufficio e magazzino.
  { superficie: "scanner", percorso: "/scanner", nome: "QBI SCANNER",
    descrizione: "Scanner di documenti dal telefono: ogni documento va nella sezione giusta di QBI", manifest: "/scanner.webmanifest", funzioni: ["scanner.documenti"] },
];

/**
 * QBI Suite (ufficio) installabile come app sul computer, sul tablet e sul telefono (Luan 30/10/2026 17:02). Ambito proprio, /ufficio, mai la
 * radice del sito: un'app con ambito «/» comprenderebbe anche /autista e /magazzino e il telefono le confonderebbe («già installata»).
 * L'ufficio si apre sia da / sia da /ufficio; l'app installata parte da /ufficio.
 */
export const UFFICIO = { percorso: "/ufficio", nome: "QBI Suite", manifest: "/ufficio.webmanifest", icona: "/qbi-192.png" };
const ICONE: Record<string, string> = { "/autista": "/onroad-192.png", "/magazzino": "/onmag-192.png", "/live": "/qbi-192.png", "/posta": "/posta-192.png", "/scanner": "/scanner-192.png" };

/** Pagina della web app d'ufficio («/» o «/ufficio»): qui si collega il manifest di QBI Suite. */
export function ePaginaUfficio(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "").toLowerCase();
  return p === "" || p === UFFICIO.percorso;
}

/** Superficie richiesta dall'indirizzo della pagina; null = web app d'ufficio. */
export function superficieDaPercorso(pathname: string): InfoSuperficie | null {
  const p = pathname.replace(/\/+$/, "").toLowerCase();
  return SUPERFICI.find((s) => s.percorso === p) ?? null;
}

/**
 * Manifest e nome dell'app installata, decisi dall'indirizzo al caricamento della pagina (prima dell'accesso).
 * Ogni app ha il suo manifest con id, start_url e scope propri: il telefono le installa come app diverse
 * (segnalazione di Luan del 30/10/2026: con QBI ON MAG installata, QBI ON ROAD risultava «già installata»).
 * Ufficio («/», «/ufficio»): manifest di QBI Suite. /live: manifest proprio (ambito /live, a schermo intero sul PC del megaschermo, R3f).
 */
export function impostaManifest(pathname: string, doc: Document = document): void {
  const s = superficieDaPercorso(pathname);
  doc.querySelectorAll('link[rel="manifest"], link[rel="apple-touch-icon"], meta[name="apple-mobile-web-app-title"], meta[name="apple-mobile-web-app-capable"]')
    .forEach((e) => e.remove());
  const app = s?.manifest ? { nome: s.nome, manifest: s.manifest, icona: ICONE[s.percorso] }
    : !s && ePaginaUfficio(pathname) ? UFFICIO : null;
  if (!app) return;
  const aggiungi = (tag: string, attr: Record<string, string>) => { const e = doc.createElement(tag); Object.assign(e, attr); doc.head.appendChild(e); };
  aggiungi("link", { rel: "manifest", href: app.manifest });
  // iPhone e iPad: nome e icona sotto la schermata Home, apertura a schermo intero
  if (app.icona) aggiungi("link", { rel: "apple-touch-icon", href: app.icona });
  aggiungi("meta", { name: "apple-mobile-web-app-title", content: app.nome });
  aggiungi("meta", { name: "apple-mobile-web-app-capable", content: "yes" });
  doc.title = app.nome;
}
